import {
  address as parseSolanaAddress,
  getAddressEncoder,
  getProgramDerivedAddress,
} from '@solana/kit';
import { useEffect, useState } from 'react';

import { logger } from '@/lib/logger';

import { SVM_SUPERSTATE_TOKEN_ALLOWLIST } from '@/constants/addresses';

import type { SolanaAddress } from '@/utils/models';
import { getSolanaRpc } from '@/utils/solana';

const addressEncoder = getAddressEncoder();
// UTF-8 bytes of the literal seed, byte-identical to web3.js `Buffer.from('public_allowed_account')`.
const PUBLIC_ALLOWED_ACCOUNT_SEED = new TextEncoder().encode('public_allowed_account');

/**
 * Derive the public allowed account PDA (mirrors the @superstateinc/allowlist derivation).
 */
async function getPublicAllowedAccountPda(
  userAccount: SolanaAddress,
  programId: SolanaAddress,
): Promise<SolanaAddress> {
  const [pda] = await getProgramDerivedAddress({
    programAddress: programId,
    seeds: [PUBLIC_ALLOWED_ACCOUNT_SEED, addressEncoder.encode(userAccount)],
  });
  return pda;
}

/**
 * Hook to check if a Solana address is allowed for Superstate tokens on Solana
 */
export const useSuperstateSolanaAllowlist = (address?: SolanaAddress) => {
  const [data, setData] = useState<boolean | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!address) {
      setData(undefined);
      return;
    }

    const checkAllowlist = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const programId = parseSolanaAddress(SVM_SUPERSTATE_TOKEN_ALLOWLIST);
        const publicAllowlistPda = await getPublicAllowedAccountPda(address, programId);

        // Check if the account exists (which means the address is on the public allowlist)
        const { value } = await getSolanaRpc()
          .getAccountInfo(publicAllowlistPda, { encoding: 'base64' })
          .send();

        // If the account exists, the address is on the allowlist
        setData(value !== null);
      } catch (err) {
        logger.error('Error checking Solana allowlist:', err);
        setError(err as Error);
        setData(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAllowlist();
  }, [address]);

  return { data, isLoading, error };
};
